/**
 * Sidebar / kicker display helpers.
 *
 * The SDK exports verbose function names like `claimBorrower` and
 * `createContractThread` so the global namespace stays unambiguous. In the
 * UI those reads as redundant — the group column already says "borrower",
 * so saying "claim" is enough for navigation. The full name still lives in
 * the page H1 and in the generated code snippet.
 *
 * Some groups also have subgroups (currently `contract` has `thread` and
 * `fleet`) so we need to surface that hierarchy explicitly rather than
 * piling everything in the parent group.
 */

import type { InstructionGroup } from '../data/instructions.generated';

/** Subgroup names known per parent group. Empty record = no subgroups. */
const SUBGROUPS_FOR: Partial<Record<InstructionGroup, readonly string[]>> = {
  contract: ['Thread', 'Fleet'],
};

/**
 * Aliases that route a name into a *nested* subgroup path. The value
 * is a `/`-separated path of subgroup ids (lower-case). The first
 * segment must already exist in `SUBGROUPS_FOR[group]`. Examples:
 *
 *   `Fiber` → `thread/fiber` puts `fetchFiber` two levels deep:
 *   contract → thread → fiber.
 *
 * The alias token IS stripped from the display so labels read as
 * bare verbs (`fetchFiber` → `fetch` inside the fiber sub-sub).
 */
const SUBGROUP_ALIASES: Record<string, string> = {
  Fiber: 'thread/fiber',
};

export interface DisplayInfo {
  /** Short label rendered in the sidebar + kicker. */
  display: string;
  /**
   * Optional subgroup path (e.g. `thread`, `thread/fiber`). Empty
   * string / undefined = direct under the parent group. Single
   * segment = one level of nesting; `/` separates further levels.
   */
  subgroup?: string;
}

export function displayInfo(name: string, group: InstructionGroup): DisplayInfo {
  // Subgroup detection — the Pascal-case token inside the function name maps
  // to the subgroup. Strip both `(Contract)?Subgroup` so e.g.
  // `createContractThread` and `fetchThread` both display as the verb.
  const subs = SUBGROUPS_FOR[group] ?? [];
  for (const sub of subs) {
    if (name.includes(sub)) {
      const stripped = name.replace(new RegExp(`(Contract)?${sub}`), '');
      return {
        display: stripped || name,
        subgroup: sub.toLowerCase(),
      };
    }
  }

  // Aliased subgroup tokens — route to a (possibly nested) subgroup
  // path. The first segment of the path must be a real subgroup of
  // this group; deeper segments form nested subsections.
  for (const [aliasToken, path] of Object.entries(SUBGROUP_ALIASES)) {
    const [primarySub] = path.split('/');
    const realSubs = subs.map(s => s.toLowerCase());
    if (!realSubs.includes(primarySub)) continue;
    if (name.includes(aliasToken)) {
      const stripped = name.replace(new RegExp(`(Contract)?${aliasToken}`), '');
      return { display: stripped || name, subgroup: path };
    }
  }

  // Generic case: drop the trailing capitalised group name.
  const cap = group.charAt(0).toUpperCase() + group.slice(1);
  if (name.endsWith(cap) && name.length > cap.length) {
    return { display: name.slice(0, -cap.length) };
  }
  return { display: name };
}

export const SUBGROUP_LABEL: Record<string, string> = {
  thread: 'Thread',
  fleet: 'Fleet',
  fiber: 'Fiber',
};
